import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Search } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const badge = (status) =>
  status === "confirmed" ? "text-[#8FD19E] border-[#8FD19E]/40" : status === "unsubscribed" ? "text-[#F5B7B1] border-[#F5B7B1]/40" : "text-[#94A3B8] border-[#94A3B8]/40";

/** Email list (consent + double opt-in state) and GDPR erasure. */
export const MembersPanel = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/admin/members").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const erase = async (email) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/people/erase", { email });
      const n = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${n} record(s) for ${email}`);
      setConfirm(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const visible = rows.filter((m) => m.email.includes(q.toLowerCase()));

  return (
    <div data-testid="members-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
        <label className="flex items-center gap-2 flex-1 border border-[#D4AF37]/25 bg-[#070A0F] px-3">
          <Search size={14} className="text-[#D4AF37]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email…"
            className="w-full bg-transparent py-3 text-sm text-[#F7F8F9] placeholder:text-[#94A3B8]/50 focus:outline-none" data-testid="members-search" />
        </label>
        <p className="text-[11px] text-[#94A3B8]" data-testid="members-count">{rows.length} people · {rows.filter((m) => m.subscription?.status === "confirmed").length} confirmed subscribers</p>
      </div>
      <p className="text-[11px] text-[#94A3B8]/70 mt-3">
        Erasure removes the person's account, votes, applications (and their public card + photo), charter requests and consent record. Use it when someone asks you to delete their data.
      </p>
      <ul className="space-y-2 mt-5" data-testid="members-list">
        {visible.length === 0 && <li className="text-sm text-[#94A3B8] font-editorial italic" data-testid="members-empty">No one matches.</li>}
        {visible.map((m) => (
          <li key={m.id} className="border border-[#D4AF37]/15 bg-[#0B111B] px-4 md:px-6 py-3 flex flex-wrap items-center gap-3" data-testid={`member-row-${m.id}`}>
            <span className="font-coords text-xs text-[#F5F3EF] flex-1 min-w-[200px] break-all">{m.email}</span>
            <span className={`text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${badge(m.subscription?.status)}`} data-testid={`member-sub-${m.id}`}>
              {m.subscription?.status || "no list consent"}
            </span>
            <span className="text-[10px] text-[#94A3B8] hidden md:block">
              {m.consent ? `consent ${new Date(m.consent.accepted_at).toLocaleString()} · ${m.consent.version}` : "—"}
            </span>
            {confirm === m.email ? (
              <span className="flex items-center gap-2">
                <button onClick={() => erase(m.email)} disabled={busy}
                  className="text-[10px] tracking-[0.2em] uppercase bg-[#F5B7B1] text-[#070A0F] px-3 py-1.5 disabled:opacity-50" data-testid={`member-delete-confirm-${m.id}`}>
                  {busy ? "Deleting…" : "Yes, delete everything"}
                </button>
                <button onClick={() => setConfirm(null)} className="text-[10px] tracking-[0.2em] uppercase text-[#94A3B8]" data-testid={`member-delete-cancel-${m.id}`}>Cancel</button>
              </span>
            ) : (
              <button onClick={() => setConfirm(m.email)}
                className="flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#F5B7B1] hover:text-[#F7F8F9] transition-colors" data-testid={`member-delete-${m.id}`}>
                <Trash2 size={12} /> Delete data
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
